import { CartAddEvent } from '@theme/events';

/*
 * Groups 1 loose Master + 2 loose charms into a pack. The Cart Transform only merges lines that share a
 * `_bundle_id` and the charms must be nested under the Master (parent_id), which can only be set when a
 * line is added - so the loose lines are removed and re-added as a pack.
 *
 * That swap used to be 3 sequential /cart/change.js calls followed by /cart/add.js (~2-4 s with the cart
 * partly empty). Opening the cart or leaving the page in that window showed missing items, or lost them
 * for good. Now:
 * - the loose lines are removed in one /cart/update.js call (by line key, so concurrent adds can't shift them);
 * - the pack about to be re-added is saved in localStorage first, and re-added on the next page load if
 *   the page was left before the add finished;
 * - links to the cart and checkout wait for a pending regroup before navigating.
 */
const PENDING_KEY = 'one-bundle-pending';
const PENDING_MAX_AGE = 10 * 60 * 1000;
// A cart change this recent may still have a regroup coming (product forms check 200 ms after adding).
const RECENT_CHANGE_MS = 1500;

export class CartBundlingManager {
    /*
     * Every product form on the page asks for a bundling check when it mounts and on every cart
     * update, so a collection with 48 cards fired dozens of /cart.js requests (measured: 26 on load).
     * Calls are coalesced: requests that arrive together share one run, and a request that arrives
     * while a run is in progress triggers a single follow-up run once it finishes (the cart may have
     * changed in between). Callers still get a promise that resolves after their check is done.
     */
    static #timer = null;
    static #running = null;
    static #rerun = false;
    static #waiters = [];
    static #lastCartChange = 0;

    static checkAndApplyBundling() {
        return new Promise((resolve) => {
            this.#waiters.push(resolve);
            if (this.#running) {
                this.#rerun = true;
                return;
            }
            clearTimeout(this.#timer);
            this.#timer = setTimeout(() => this.#flush(), 50);
        });
    }

    /** Resolves the add-to-cart requests in flight (set from the forms' submit, cleared on cart:update). */
    static #addInFlight = null;

    /** True while an add or a check is in progress, or right after a cart change (a check may follow). */
    static get isBusy() {
        return (
            Boolean(this.#addInFlight || this.#timer || this.#running || this.#rerun) ||
            Date.now() - this.#lastCartChange < RECENT_CHANGE_MS
        );
    }

    /** Waits for an add-to-cart request still in flight (max 8 s), so the check below sees its item. */
    static #waitForAdd() {
        if (!this.#addInFlight) return Promise.resolve();
        return Promise.race([this.#addInFlight.promise, new Promise((resolve) => setTimeout(resolve, 8000))]);
    }

    static async #flush() {
        this.#timer = null;
        const waiters = this.#waiters.splice(0);
        this.#running = this.#run();
        const result = await this.#running;
        this.#running = null;
        waiters.forEach((resolve) => resolve(result));

        if (this.#rerun) {
            this.#rerun = false;
            this.#timer = setTimeout(() => this.#flush(), 50);
        }
    }

    static async #run() {
        try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            if (!cart.items || cart.items.length === 0) {
                return false;
            }

            const bundlingResult = await this.#checkBundlingConditions(cart.items);

            if (bundlingResult.shouldBundle) {
                await this.#applyBundling(bundlingResult.master, bundlingResult.charms);
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error("error in cart", error);
            return false;
        }

    }
    static async #checkBundlingConditions(cartItems) {
        const masters = [];
        const charms = [];
        const unbundledCharms = [];

        for (let i = 0; i < cartItems.length; i++) {
            const item = cartItems[i];

            const isMaster = item.properties?._bundle_type === 'main' && !item.properties?._bundle_id;
            const isCharm = item.properties?._bundle_type === 'charm' && !item.properties?._bundle_id;

            if (isMaster) {
            masters.push({
                cartItem: item,
                lineIndex: i + 1
            });
            } else if (isCharm) {
            charms.push({
                cartItem: item,
                lineIndex: i + 1
            });

            if (!item.parent_relationship?.parent_key) {
                unbundledCharms.push({
                cartItem: item,
                lineIndex: i + 1
                });
            }
            }
        }

        if (masters.length !== 1) {
            return {
            shouldBundle: false,
            reason: `Expected 1 unbundled master product, found ${masters.length}`
            };
        }

        if (unbundledCharms.length < 2) {
            return {
            shouldBundle: false,
            reason: `Expected at least 2 unbundled charms, found ${unbundledCharms.length}`
            };
        }

        const master = /** @type {{ cartItem: any, lineIndex: number }} */ (masters[0]);
        const selectedCharms = unbundledCharms.slice(0, 2);

        // Bundle cost comes from the master's custom.bundle_cost metafield (in currency units), cart prices are in cents
        const bundleCost = this.#parseBundleCost(master.cartItem.properties?._bundle_cost);

        if (bundleCost !== null) {
            const itemsTotal = [master, ...selectedCharms]
                .reduce((total, entry) => total + (entry?.cartItem.final_price ?? 0), 0);

            if (itemsTotal < bundleCost) {
                return {
                    shouldBundle: false,
                    reason: `Items total (${itemsTotal}) is less than bundle price (${bundleCost})`
                };
            }
        }

        return {
            shouldBundle: true,
            master,
            charms: selectedCharms,
            reason: 'Conditions met for bundling'
        };
    }

    /** @param {string | number | undefined} value */
    static #parseBundleCost(value) {
        if (value === undefined || value === null || value === '') return null;

        const amount = parseFloat(String(value).replace(',', '.'));
        return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    }

    static async #applyBundling(master, charms) {
        try {
            const bundleId = this.#generateBundleId();

            const itemsToAdd = [];

            const masterProperties = {
                ...master.cartItem.properties,
                _bundle_id: bundleId,
                _bundle_type: 'main'
            };

            itemsToAdd.push({
                id: parseInt(master.cartItem.variant_id, 10),
                quantity: master.cartItem.quantity,
                properties: masterProperties
            });

            charms.forEach((charm, index) => {
                const charmProperties = {
                    ...charm.cartItem.properties,
                    _bundle_id: bundleId,
                    _bundle_type: 'charm',
                    _charm_position: (index + 1).toString()
                };

                itemsToAdd.push({
                    id: parseInt(charm.cartItem.variant_id, 10),
                    quantity: charm.cartItem.quantity,
                    parent_id: parseInt(master.cartItem.key, 10),
                    properties: charmProperties
                });
            });

            const removedKeys = [master.cartItem.key, ...charms.map((charm) => charm.cartItem.key)];

            // Saved before touching the cart, so a page left mid-way can finish the regroup (see #resumePending)
            this.#savePending({ bundleId, removedKeys, items: itemsToAdd, time: Date.now() });

            const removeResponse = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    updates: Object.fromEntries(removedKeys.map((key) => [key, 0]))
                }),
            });

            if (!removeResponse.ok) {
                this.#clearPending();
                const responseText = await removeResponse.text();
                throw new Error(`Failed to remove loose items: ${removeResponse.status} - ${responseText}`);
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ items: itemsToAdd }),
            });

            const responseText = await response.text();

            if (!response.ok) {
                // Put the loose items back rather than leave them out of the cart
                await this.#restoreLoose(itemsToAdd);
                this.#clearPending();
                throw new Error(`Failed to re-add items: ${response.status} - ${responseText}`);
            }

            this.#clearPending();

            const updatedCartResponse = await fetch('/cart.js');
            const updatedCart = await updatedCartResponse.json();


            this.dispatchCartUpdateEvent(updatedCart);

            const bundleEvent = new CustomEvent('cart:bundle-applied', {
                detail: {
                    bundleId,
                    master: master.cartItem,
                    charms: charms.map(c => c.cartItem)
                },
                bubbles: true
            });

            document.dispatchEvent(bundleEvent);

            return true;
        } catch (error) {
            console.error('Error applying bundle:', error);
            throw error;
        }
    }

    /** Re-adds items as loose lines (no pack properties). */
    static async #restoreLoose(items) {
        try {
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: items.map(({ id, quantity, properties }) => {
                        const { _bundle_id, _charm_position, ...rest } = properties;
                        return { id, quantity, properties: rest };
                    })
                }),
            });
        } catch (error) {
            console.error('Error restoring loose items:', error);
        }
    }

    static #savePending(pending) {
        try {
            localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
        } catch (_) {
            // Storage unavailable (private mode): the regroup still runs, it just can't be resumed
        }
    }

    static #readPending() {
        try {
            return JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
        } catch (_) {
            return null;
        }
    }

    static #clearPending() {
        try {
            localStorage.removeItem(PENDING_KEY);
        } catch (_) {
            // no-op
        }
    }

    /**
     * The previous page was left during a regroup. /cart/update.js removes all loose lines at once, so either
     * none were removed (nothing to do, the normal check regroups them) or all were - then re-add the pack.
     */
    static async #resumePending() {
        const pending = this.#readPending();
        if (!pending) return false;
        this.#clearPending();
        if (!Array.isArray(pending.items) || Date.now() - pending.time > PENDING_MAX_AGE) return false;

        try {
            const cart = await fetch('/cart.js').then((response) => response.json());
            const keys = new Set(cart.items.map((item) => item.key));
            const looseStillThere = pending.removedKeys.some((key) => keys.has(key));
            const packAdded = cart.items.some((item) => item.properties?._bundle_id === pending.bundleId);
            if (looseStillThere || packAdded) return false;

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: pending.items }),
            });
            if (!response.ok) return false;

            this.dispatchCartUpdateEvent(await fetch('/cart.js').then((r) => r.json()));
            return true;
        } catch (error) {
            console.error('Error resuming bundle:', error);
            return false;
        }
    }

    /**
     * Runs once per page: finishes an interrupted regroup, checks the cart page (it has no product form to
     * trigger the check) and makes cart/checkout links wait for a pending regroup.
     */
    static init() {
        if (this.initialized) return;
        this.initialized = true;

        document.addEventListener('cart:update', () => {
            this.#lastCartChange = Date.now();
            this.#addInFlight?.resolve();
            this.#addInFlight = null;
        });

        // An add-to-cart form was submitted: until its cart:update arrives, a click on the cart would open
        // it without the new item (and before any regroup has started).
        document.addEventListener(
            'submit',
            (event) => {
                const form = event.target;
                if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
                if (!this.#addInFlight) {
                    let resolve;
                    const promise = new Promise((done) => (resolve = done));
                    this.#addInFlight = { promise, resolve };
                }
            },
            true
        );

        // Both dispatch a cart update when they change the cart, which re-renders the cart page/drawer
        const isCartPage = document.querySelector('main[data-template^="cart"]') !== null;
        const resumed = this.#readPending() ? this.#resumePending() : Promise.resolve(false);
        resumed.then((didResume) => {
            if (!didResume && isCartPage) this.checkAndApplyBundling();
        });

        document.addEventListener('click', (event) => this.#holdNavigation(event), true);
    }

    /** Cart and checkout links/buttons wait until a pending regroup has finished. */
    static #holdNavigation(event) {
        if (!this.isBusy || !(event.target instanceof Element)) return;

        const link = event.target.closest('a[href]');
        const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');

        let go = null;
        if (link) {
            // /cart, /es/cart..., and checkout links
            const url = new URL(link.href, window.location.href);
            const path = url.pathname.replace(/\/$/, '');
            if (url.origin === window.location.origin && (path.endsWith('/cart') || path.includes('/checkout'))) {
                go = () => window.location.assign(url.href);
            }
        } else if (checkoutButton) {
            const form = checkoutButton.form || checkoutButton.closest('form');
            if (form) go = () => form.requestSubmit(checkoutButton);
        }
        if (!go) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        document.documentElement.style.cursor = 'progress';
        this.#waitForAdd()
            .then(() => this.checkAndApplyBundling())
            .finally(go);
    }

    static async dispatchCartUpdateEvent(cartData) {
        try {

            document.dispatchEvent(
                new CartAddEvent(cartData, 'cart-bundle-manager', {
                    didError: false,
                    source: 'cart-bundle-manager',
                    // Absolute cart count (cart-icon replaces its count with this)
                    itemCount: cartData.item_count ?? 0,
                    productId: cartData.items?.[0]?.product_id,
                })
            );

            const cartUpdateEvent = new CustomEvent('cart:updated', {
                detail: {
                    resource: cartData,
                    sourceId: 'cart-bundle-manager'
                },
                bubbles: true
            });

            document.dispatchEvent(cartUpdateEvent);
        } catch (error) {
            console.error('Error dispatching cart event:', error);
        }
    }

    static #generateBundleId() {
        return Date.now().toString(36) + Math.random().toString(36).substring(2);
    }
}

CartBundlingManager.init();
